import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import type {
  AdminUser,
  CreateUserRequest,
  UpdateUserRequest,
  UserListResponse,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || 'An error occurred';
    }
  }
  return 'An error occurred';
};

export const useListUsers = (skip = 0, limit = 10, search?: string) => {
  return useQuery({
    queryKey: ['users', skip, limit, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());
      if (search) {
        params.append('search', search);
      }
      const { data } = await apiClient.get<UserListResponse>(
        `/users?${params.toString()}`
      );
      return data;
    },
  });
};

export const useGetUser = (userId: string) => {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminUser>(`/users/${userId}`);
      return data;
    },
    enabled: !!userId,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: CreateUserRequest) => {
      const { data } = await apiClient.post<AdminUser>('/users', payload);
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: `User ${data.first_name} ${data.last_name} created successfully`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to create user',
        type: 'error',
      });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { userId: string; data: UpdateUserRequest }) => {
      const { data } = await apiClient.put<AdminUser>(
        `/users/${payload.userId}`,
        payload.data
      );
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: `User ${data.first_name} ${data.last_name} updated successfully`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to update user',
        type: 'error',
      });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await apiClient.delete(`/users/${userId}`);
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'User deleted successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to delete user',
        type: 'error',
      });
    },
  });
};
